import { responseType } from '../../../common/type/ResponseType';
import { UserInfoType } from '../../../common/type/UserInfoType';
import { removeLocalStorage, setLocalStorage } from '../../../common/utils/localStorageHandler';
import { getSessionStorage } from '../../../common/utils/sessionStorageHandler';
import { nonNullable } from '../../../common/utils/nonNullable';
import { isResponseTypeSessionNotExist } from '../../../common/utils/responseTypeChecker';
import { redirectToLogin } from '../../../common/utils/redirectToLogin';
import {
	localStorageKeyCompanyCode,
	localStorageKeyUserCode,
	permission,
	sessionStorageKeyAuthorizationToken,
	unknownLabel,
} from '../../../common/const/commonConst';
import { determinePermission } from '../../../common/utils/determinePermission';

import { RegisterAccountInput } from '../../../API';
import { queryRegisterAccount } from '../api/queryRegisterAccount';
import { G500LiteAuthInfoType, OffsegAuthInfoType } from '../type/AuthInfoType';
import { LoginResponseType } from '../type/LoginResponseType';

export const registerHandler = async (
	offsegAuthInfo: OffsegAuthInfoType,
	g500LiteAuthInfo: G500LiteAuthInfoType,
	isChecked: boolean,
	setUserInfo: React.Dispatch<React.SetStateAction<UserInfoType>>,
): Promise<LoginResponseType> => {
	// 会社IDのバリデーションチェック（null、最大桁数、半角英数字記号）
	if (!g500LiteAuthInfo.companyId || [...g500LiteAuthInfo.companyId].length >= 256 || /[^\x21-\x7e]/.test(g500LiteAuthInfo.companyId)) {
		console.error('validation error');
		return [responseType.validationError, permission.id.unknown];
	}
	// ログインIDのバリデーションチェック（null、最大桁数、半角英数字記号）
	if (!g500LiteAuthInfo.loginId || [...g500LiteAuthInfo.loginId].length >= 65 || /[^\x21-\x7e]/.test(g500LiteAuthInfo.loginId)) {
		console.error('validation error');
		return [responseType.validationError, permission.id.unknown];
	}
	// パスワードのバリデーションチェック（null、最大桁数、半角英数字記号）
	if (!g500LiteAuthInfo.password || [...g500LiteAuthInfo.password].length >= 41 || /[^\x21-\x7e]/.test(g500LiteAuthInfo.password)) {
		console.error('validation error');
		return [responseType.validationError, permission.id.unknown];
	}

	try {
		const authorizationToken = getSessionStorage(sessionStorageKeyAuthorizationToken);

		if (!authorizationToken) {
			return [responseType.systemError, permission.id.unknown];
		}

		const registerAccountInput: RegisterAccountInput = {
			companyId: g500LiteAuthInfo.companyId,
			loginId: g500LiteAuthInfo.loginId,
			password: g500LiteAuthInfo.password,
			authorizationToken: authorizationToken,
		};

		// ログイン情報登録APIを実行
		const [isApiError, registerAccountResult] = await queryRegisterAccount(registerAccountInput, authorizationToken);

		if (isApiError || !registerAccountResult) {
			return [responseType.systemError, permission.id.unknown];
		}

		if (isResponseTypeSessionNotExist(registerAccountResult)) {
			redirectToLogin();
		}

		switch (registerAccountResult.registerAccount.responseType) {
			case responseType.notExistApiResponseData:
				return [responseType.notExistApiResponseData, permission.id.unknown];
			case responseType.apiError:
				return [responseType.apiError, permission.id.unknown];
			case responseType.dbError:
				return [responseType.dbError, permission.id.unknown];
			case responseType.systemError:
				return [responseType.systemError, permission.id.unknown];
		}

		const loginUserInfo = registerAccountResult.registerAccount.loginUserInfo.filter(nonNullable)[0];

		// 共通ウェブポータルサービスの権限を判定
		const [isError, permissionNum] = determinePermission(loginUserInfo.permissionName, loginUserInfo.authority);

		// エラーチェック
		if (isError) {
			return [responseType.systemError, permission.id.unknown];
		}

		if (isChecked) {
			setLocalStorage(localStorageKeyCompanyCode, offsegAuthInfo.companyCode);
			setLocalStorage(localStorageKeyUserCode, offsegAuthInfo.userCode);
		} else {
			removeLocalStorage(localStorageKeyCompanyCode);
			removeLocalStorage(localStorageKeyUserCode);
		}

		const userInfo = {
			isLogin: true,
			companyName: loginUserInfo.companyName || unknownLabel,
			userName: loginUserInfo.userName || unknownLabel,
			permission: permissionNum,
		};

		setUserInfo(userInfo);

		return [responseType.success, permissionNum];
	} catch (error) {
		// WebStorageErrorについてはloginHandlerでハンドリングするため、ここでは処理しない
		console.error(error);
		return [responseType.systemError, permission.id.unknown];
	}
};
